import { useState } from 'react';
import { LabelTemplate, PlacedBlock, SocketConnection, TextAnnotation } from '@/app/types/cad';
import { Plus, Trash2, Edit, Search } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { ScrollArea } from '@/app/components/ui/scroll-area';
import { LabelDesignerDialog } from './LabelDesignerDialog';
import { useLanguage } from '@/app/contexts/LanguageContext';

interface LabelLibraryPanelProps {
  templates: LabelTemplate[];
  onTemplateAdd: (template: LabelTemplate) => void;
  onTemplateUpdate: (template: LabelTemplate) => void;
  onTemplateDelete: (templateId: string) => void;
  onTemplateSelect: (template: LabelTemplate) => void;
  selectedTemplateId?: string;
  blocks?: PlacedBlock[]; // Für Attribut-Extraktion
  connections?: SocketConnection[]; // Für Attribut-Extraktion
  textAnnotations?: TextAnnotation[]; // Für Attribut-Extraktion
}

export function LabelLibraryPanel({
  templates,
  onTemplateAdd,
  onTemplateUpdate,
  onTemplateDelete,
  onTemplateSelect,
  selectedTemplateId,
  blocks = [],
  connections = [],
  textAnnotations = []
}: LabelLibraryPanelProps) {
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [designerOpen, setDesignerOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<LabelTemplate | undefined>();

  const filteredTemplates = templates.filter(template =>
    template.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleNewTemplate = () => {
    setEditingTemplate(undefined);
    setDesignerOpen(true);
  };

  const handleEditTemplate = (template: LabelTemplate) => {
    setEditingTemplate(template);
    setDesignerOpen(true);
  };

  const handleSaveTemplate = (template: LabelTemplate) => {
    if (editingTemplate) {
      onTemplateUpdate(template);
    } else {
      onTemplateAdd(template);
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-3 border-b space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">{t('sidebar.labels.title')}</h3>
          <Button size="sm" onClick={handleNewTemplate}>
            <Plus className="w-4 h-4 mr-1" />
            {t('common.new')}
          </Button>
        </div>
        
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('sidebar.labels.search')}
            className="pl-8"
          />
        </div>
      </div>

      {/* Templates List */}
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-2">
          {filteredTemplates.length === 0 ? (
            <div className="text-center text-gray-500 py-8 text-sm">
              {searchTerm ? t('sidebar.labels.notFound') : t('sidebar.labels.empty')}
            </div>
          ) : (
            filteredTemplates.map(template => (
              <LabelLibraryItem
                key={template.id}
                template={template}
                isSelected={template.id === selectedTemplateId}
                onSelect={() => onTemplateSelect(template)}
                onEdit={() => handleEditTemplate(template)}
                onDelete={() => onTemplateDelete(template.id)}
              />
            ))
          )}
        </div>
      </ScrollArea>

      {/* Designer Dialog */}
      <LabelDesignerDialog
        open={designerOpen}
        onOpenChange={setDesignerOpen}
        template={editingTemplate}
        onSave={handleSaveTemplate}
        blocks={blocks}
        connections={connections}
        textAnnotations={textAnnotations}
      />
    </div>
  );
}

interface LabelLibraryItemProps {
  template: LabelTemplate;
  isSelected: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

function LabelLibraryItem({
  template,
  isSelected,
  onSelect,
  onEdit,
  onDelete
}: LabelLibraryItemProps) {
  const { t } = useLanguage();
  return (
    <div
      className={`
        border rounded-lg p-3 cursor-pointer transition-all
        ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}
      `}
      onClick={onSelect}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <h4 className="font-medium text-sm">{template.name}</h4>
          <p className="text-xs text-gray-500 mt-1">
            {t('sidebar.labels.rows').replace('{count}', template.rows.length.toString()).replace('{suffix}', template.rows.length !== 1 ? 'n' : '')} × {t('sidebar.labels.columns').replace('{count}', template.columns.toString()).replace('{suffix}', template.columns !== 1 ? 'n' : '')}
          </p>
        </div>
        
        <div className="flex gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            className="p-1 hover:bg-white rounded"
            title={t('common.edit')}
          >
            <Edit className="w-3.5 h-3.5 text-gray-600" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (confirm(t('sidebar.labels.confirmDelete').replace('{name}', template.name))) {
                onDelete();
              }
            }}
            className="p-1 hover:bg-white rounded"
            title={t('common.delete')}
          >
            <Trash2 className="w-3.5 h-3.5 text-red-600" />
          </button>
        </div>
      </div>

      {/* Mini Preview */}
      <div className="bg-white rounded border p-2 overflow-hidden">
        <table style={{ borderCollapse: 'collapse', fontSize: '8px', width: '100%' }}>
          <tbody>
            {template.rows.slice(0, 3).map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, colIndex) => (
                  <td
                    key={colIndex}
                    style={{
                      border: `1px solid ${template.borderColor}`,
                      backgroundColor: template.backgroundColor,
                      color: template.textColor,
                      padding: '2px 4px',
                      textAlign: 'left',
                      verticalAlign: 'middle',
                      maxWidth: '60px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {cell.type === 'attribute' 
                      ? `{${cell.content || '?'}}` 
                      : (cell.content || '-')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {template.rows.length > 3 && (
          <div className="text-center text-gray-400 text-xs mt-1">
            {t('sidebar.labels.more').replace('{count}', (template.rows.length - 3).toString())}
          </div>
        )}
      </div>
    </div>
  );
}